import { useSupplyUiConfig } from '../invoice-fields'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CheckSquareOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { Alert, Button, Empty, Result, Skeleton, Spin, Tooltip, Typography, message } from 'antd'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import fetchAPI from '../../../../lib/utils/fetch-api'
import B2BLayout from '../../../layouts/b2b'
import { buildMcpUiUrl, getAi2UiBaseUrl } from '../../../utils/mcp-ui'
import {
  createMcpEmbedTokenBroker,
  isUsableMcpEmbedToken,
  McpEmbedToken,
  McpEmbedTokenBroker,
} from '../../ai-reports/embed-token-broker'
import SupplyReviewWorkbench from './workbench'
import { parseRouting, ReviewDocument, SourceFile } from './result-model'
import { ReviewPacketSummary, reviewMessageActionCard } from './message-actions'
import './styles.css'

type Review = {
  id: string
  chat_id: string | null
  creator_name?: string | null
  created_at: string
  updated_at: string
}

const TooltipResultError = ({ text }: { text: string }) => <Tooltip title={text}><Typography.Text type="danger">Результат недоступен</Typography.Text></Tooltip>

export const SupplyReviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { search, hash } = useLocation()
  const reviewQuery = useMemo(() => new URLSearchParams(search), [search])
  const focusedDocumentId = reviewQuery.get('document') || undefined
  const listSearch = reviewQuery.get('list') || ''
  const fromChat = reviewQuery.get('from') === 'chat'
  const incomingListPath = `/warehouse/incoming${listSearch ? `?${listSearch}` : ''}`
  const uiConfig = useSupplyUiConfig()
  const canOpenChat = uiConfig.can_open_chat
  const sessionData = useSelector((state: any) => state.session?.sessionData)
  const currentUserId = String(sessionData?.user?.id || '').trim()
  const [review, setReview] = useState<Review | null>(null)
  const [view, setView] = useState<'chat' | 'check'>('check')
  const [documents, setDocuments] = useState<ReviewDocument[]>([])
  const [sourceFiles, setSourceFiles] = useState<SourceFile[]>([])
  const [resultsError, setResultsError] = useState('')
  const [resultsLoading, setResultsLoading] = useState(false)
  const currentReviewRef = useRef('')
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const currentChatId = review?.chat_id || ''
  const [reviews, setReviews] = useState<Review[]>([])
  const [chatNames, setChatNames] = useState<Record<string, string>>({})
  const chatIds = reviews.map(item => item.chat_id).filter(Boolean).join(',')
  useEffect(() => {
    if (!chatIds) return
    let active = true
    void fetchAPI(`/api/mcp2/codex/agents/titles?${new URLSearchParams({ ids: chatIds })}`).then(result => {
      if (!active || !Array.isArray(result?.data)) return
      setChatNames(previous => ({ ...previous, ...Object.fromEntries(result.data.map(item => [item.id, item.name])) }))
    }).catch(() => {})
    return () => { active = false }
  }, [chatIds, reviews])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [listError, setListError] = useState('')
  const [frameLoading, setFrameLoading] = useState(true)
  const [frameVersion, setFrameVersion] = useState(0)
  const [purposeId, setPurposeId] = useState<string | undefined>()
  const [purposeError, setPurposeError] = useState('')
  const [creatingChat, setCreatingChat] = useState(false)
  const [creatingReview, setCreatingReview] = useState(false)
  const [refreshingReviews, setRefreshingReviews] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const tokenRef = useRef<McpEmbedToken | null>(null)
  const tokenRequestRef = useRef<Promise<McpEmbedToken | null> | null>(null)
  const brokerRef = useRef<McpEmbedTokenBroker | null>(null)
  const creatingReviewRef = useRef(false)
  const creatingChatRef = useRef(false)
  const processedMessagesRef = useRef<Set<string>>(new Set())
  const packetRequestsRef = useRef(new Map<string, Promise<ReviewPacketSummary | null>>())
  const targetOrigin = useMemo(() => new URL(getAi2UiBaseUrl() || window.location.origin, window.location.origin).origin, [])

  useEffect(() => {
    tokenRef.current = null
    tokenRequestRef.current = null
    const broker = createMcpEmbedTokenBroker({ userId: `${currentUserId}:supply:${currentChatId}` })
    brokerRef.current = broker
    return () => {
      broker?.close()
      brokerRef.current = null
    }
  }, [currentUserId, currentChatId])

  const loadToken = useCallback(async () => {
    if (isUsableMcpEmbedToken(tokenRef.current)) return tokenRef.current
    if (tokenRequestRef.current) return tokenRequestRef.current
    tokenRequestRef.current = (async () => {
      const shared = await brokerRef.current?.requestToken()
      if (isUsableMcpEmbedToken(shared)) return shared
      const response = await fetchAPI(`/api/mcp2/embed-token${currentChatId ? `?shared_chat_id=${encodeURIComponent(currentChatId)}` : ''}`)
      const token = response?.data as McpEmbedToken | undefined
      return isUsableMcpEmbedToken(token) ? token : null
    })().finally(() => { tokenRequestRef.current = null })
    const token = await tokenRequestRef.current
    if (token) {
      tokenRef.current = token
      brokerRef.current?.publishToken(token)
    }
    return token
  }, [currentChatId])

  const sendHostConfig = useCallback(async () => {
    const frame = iframeRef.current?.contentWindow
    if (!frame) return
    try {
      const token = await loadToken()
      if (!token) throw new Error('Не удалось получить доступ к чату')
      const apiBase = `${window.location.origin}/api/mcp2`
      const wsBase = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/mcp2/ws`
      frame.postMessage({
        type: 'mcp-ui:host-config',
        payload: {
          accessControlEnabled: true,
          dealerMappingEnabled: false,
          assistantMessageEventsEnabled: true,
          messageActionsEnabled: true,
          apiBase,
          wsBase,
          navigationBase: window.location.origin,
          accessToken: token.accessToken,
          accessTokenExpiresAt: token.expiresAt,
          theme: { bg: '#ffffff', surface: '#ffffff' },
        },
      }, targetOrigin)
    } catch (cause: any) {
      message.error(cause.message || 'Чат недоступен')
    }
  }, [loadToken, targetOrigin])

  useEffect(() => {
    let active = true
    const load = async () => {
      if (!uiConfig.ready) return
      if ((!id || id === 'new') && !canOpenChat) { navigate(incomingListPath, { replace: true }); return }
      setLoading(true)
      setError('')
      setListError('')
      try {
        if (id === 'new') {
          if (active) navigate('/warehouse/reviews', { replace: true })
          return
        }
        const listPromise = fetchAPI('/api/supply/reviews')
          .then(result => Array.isArray(result?.data) ? result.data as Review[] : [])
          .catch(cause => {
            if (active) setListError(cause.message || 'Не удалось загрузить список чатов')
            return null
          })
        if (!id) {
          const list = await listPromise
          if (active && list) {
            setReviews(list)
            setReview(null)
            if (list.length) navigate(`/warehouse/reviews/${list[0].id}`, { replace: true })
          }
          return
        }
        const [result, list] = await Promise.all([
          fetchAPI(`/api/supply/reviews/${encodeURIComponent(id)}`),
          listPromise,
        ])
        if (active) {
          const selected = result.data as Review
          setReview(selected)
          setFrameLoading(true)
          if (list) setReviews(list.some(item => item.id === selected.id) ? list : [selected, ...list])
        }
      } catch (cause: any) {
        if (active) setError(cause.message || 'Не удалось открыть разбор')
      } finally {
        if (active) setLoading(false)
      }
    }
    void load()
    return () => { active = false }
  }, [id, navigate, uiConfig.ready, canOpenChat, incomingListPath])

  useEffect(() => {
    let active = true
    setPurposeError('')
    fetchAPI('/api/supply/review-agent')
      .then(result => {
        if (active) setPurposeId(String(result?.data?.purpose_id || '') || undefined)
      })
      .catch(cause => { if (active) setPurposeError(cause.message || 'Агент разбора документов недоступен') })
    return () => { active = false }
  }, [])

  const loadResults = useCallback(async () => {
    if (!review?.id) return
    const reviewId = review.id
    const result = await fetchAPI(`/api/supply/reviews/${reviewId}/documents${focusedDocumentId ? `?document_id=${encodeURIComponent(focusedDocumentId)}` : ''}`)
    if (result.error || result.errors) throw new Error(result.error || result.errors.join(', '))
    const loaded: ReviewDocument[] = Array.isArray(result.data) ? result.data : []
    const files: SourceFile[] = Array.isArray(result.files) ? result.files : []
    if (currentReviewRef.current === `${reviewId}:${focusedDocumentId || ''}`) {
      setDocuments(loaded)
      if (files.length) setSourceFiles(files)
    }
    return { documents: loaded, files }
  }, [review?.id, focusedDocumentId])

  const syncResults = useCallback(async () => {
    if (!review?.id) return
    const reviewId = review.id
    setResultsLoading(true)
    try {
      const saved = focusedDocumentId ? await loadResults() : undefined
      const savedSources = saved?.documents.map(document => document.original) || []
      if (savedSources.length && savedSources.every(document => document.source_file && saved?.files.some(file => file.name === document.source_file))) {
        if (currentReviewRef.current === `${reviewId}:${focusedDocumentId || ''}`) setResultsError('')
        return
      }
      if (!review.chat_id) return
      const result = await fetchAPI(`/api/mcp2/codex/agents/${review.chat_id}?message_limit=100`)
      let messages = Array.isArray(result?.data?.messages) ? result.data.messages : []
      const assistant = [...messages].reverse().find(item => item.role === 'assistant' && parseRouting(item.content))
      const routing = assistant ? parseRouting(assistant.content) : null
      const sources = focusedDocumentId ? savedSources : routing?.documents || []
      const requiredFiles = new Set(sources.map(document => document.source_file).filter(Boolean))
      let pagination = result?.data?.message_pagination
      const collectFiles = () => messages.flatMap(item => item.role === 'user' && Array.isArray(item.metadata?.attachments) ? item.metadata.attachments : []).filter(file => typeof file.name === 'string' && typeof file.path === 'string')
      while (pagination?.has_more && [...requiredFiles].some(name => !collectFiles().some(file => file.name === name))) {
        const page = await fetchAPI(`/api/mcp2/codex/agents/${review.chat_id}/messages?before=${encodeURIComponent(pagination.next_cursor)}&limit=100`)
        messages = [...(Array.isArray(page.data) ? page.data : []), ...messages]
        pagination = page.pagination
      }
      if (currentReviewRef.current !== `${reviewId}:${focusedDocumentId || ''}`) return
      const files = collectFiles()
      setSourceFiles(files.filter((file, index) => files.findIndex(other => other.path === file.path) === index))
      if (!focusedDocumentId && routing && assistant?.created_at) {
        const saved = await fetchAPI(`/api/supply/reviews/${reviewId}/result`, { method: 'POST', body: JSON.stringify({ message_id: assistant.id, created_at: assistant.created_at, documents: routing.documents }) })
        if (saved.error || saved.errors) throw new Error(saved.error || saved.errors.join(', '))
      }
      if (!focusedDocumentId) await loadResults()
      if (!focusedDocumentId && routing && assistant?.id && routing.documents.some(document => !document.conflicts?.length && document.matches.some(match => match.confidence === 'high'))) {
        const routingKey = `routing:${reviewId}:${assistant.id}`
        if (!processedMessagesRef.current.has(routingKey)) {
          const linked = await fetchAPI(`/api/supply/reviews/${reviewId}/routing`, {
            method: 'POST', body: JSON.stringify({ message_id: assistant.id, documents: routing.documents }), timeoutMs: 180000,
          })
          if (linked.error || linked.errors) throw new Error(linked.error || linked.errors.join(', '))
          processedMessagesRef.current.add(routingKey)
        }
      }
      if (currentReviewRef.current === `${reviewId}:${focusedDocumentId || ''}`) setResultsError('')
    } catch (cause: any) {
      if (currentReviewRef.current === `${reviewId}:${focusedDocumentId || ''}`) setResultsError(cause.message || 'Не удалось загрузить результат проверки')
    } finally {
      if (currentReviewRef.current === `${reviewId}:${focusedDocumentId || ''}`) setResultsLoading(false)
    }
  }, [review?.id, review?.chat_id, loadResults, focusedDocumentId])

  useEffect(() => {
    currentReviewRef.current = `${review?.id || ''}:${focusedDocumentId || ''}`
    setDocuments([]); setSourceFiles([]); setResultsError('')
    if (review?.id) { if (!focusedDocumentId) void loadResults().catch(cause => { if (currentReviewRef.current === `${review.id}:${focusedDocumentId || ''}`) setResultsError(cause.message) }); void syncResults() }
  }, [review?.id, review?.chat_id, loadResults, syncResults, focusedDocumentId])

  useEffect(() => { setView(focusedDocumentId || !canOpenChat ? 'check' : 'chat') }, [focusedDocumentId, canOpenChat])

  const createChatForReview = async (target: Review) => {
    if (!purposeId) throw new Error(purposeError || 'Агент разбора документов недоступен')
    const title = `Разбор документов · ${new Date().toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
    const created = await fetchAPI(`/api/mcp2/chat-purpose-options/${encodeURIComponent(purposeId)}/chats?embedded=1`, {
      method: 'POST',
      body: JSON.stringify({ name: title, auto_title: true }),
      timeoutMs: 180000,
    })
    const nextChatId = String(created?.data?.id || '')
    if (!nextChatId) throw new Error(created?.error || 'Чат создан без ID')
    const linked = await fetchAPI(`/api/supply/reviews/${target.id}/chat`, {
      method: 'PUT', body: JSON.stringify({ chat_id: nextChatId }),
    })
    return linked.data as Review
  }

  const createChat = async () => {
    if (!review || review.chat_id || creatingChatRef.current) return
    creatingChatRef.current = true
    setCreatingChat(true)
    try {
      const linked = await createChatForReview(review)
      setReview(linked)
      setReviews(items => items.map(item => item.id === linked.id ? linked : item))
    } catch (cause: any) {
      message.error(cause.message || 'Не удалось создать чат')
    } finally {
      creatingChatRef.current = false
      setCreatingChat(false)
    }
  }

  const createNewReview = async () => {
    if (creatingReviewRef.current || !purposeId) return
    creatingReviewRef.current = true
    setCreatingReview(true)
    let createdReview: Review | null = null
    try {
      const result = await fetchAPI('/api/supply/reviews', { method: 'POST', body: JSON.stringify({}) })
      createdReview = result.data as Review
      if (!createdReview?.id) throw new Error('Разбор создан без ID')
      const linked = await createChatForReview(createdReview)
      setReviews(items => [linked, ...items])
      navigate(`/warehouse/reviews/${linked.id}`)
    } catch (cause: any) {
      if (createdReview?.id) navigate(`/warehouse/reviews/${createdReview.id}`)
      message.error(cause.message || 'Не удалось создать новый чат')
    } finally {
      creatingReviewRef.current = false
      setCreatingReview(false)
    }
  }

  const refreshReviews = useCallback(async () => {
    setRefreshingReviews(true)
    try {
      const result = await fetchAPI('/api/supply/reviews')
      setReviews(Array.isArray(result?.data) ? result.data as Review[] : [])
      setListError('')
    } catch (cause: any) {
      setListError(cause.message || 'Не удалось обновить список чатов')
    } finally {
      setRefreshingReviews(false)
    }
  }, [])

  useEffect(() => {
    const onFocus = () => { void refreshReviews() }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refreshReviews])

  useEffect(() => {
    if (!review) return
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow || event.origin !== targetOrigin) return
      if (event.data?.type === 'mcp-ui:request-host-config') {
        void sendHostConfig()
        return
      }
      const data = event.data
      if (data?.agent_id !== review.chat_id) return
      if (data.type === 'mcp-ui:chat-updated') {
        if (typeof data.name === 'string' && data.name.trim() && data.name.length <= 200)
          setChatNames(previous => ({ ...previous, [review.chat_id!]: data.name.trim() }))
        return
      }
      if (data.type === 'mcp-ui:request-message-actions' || data.type === 'mcp-ui:invoke-message-action') {
        const messageId = data.message_id
        if (typeof messageId !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(messageId)
          || typeof data.request_id !== 'string' || data.request_id.length > 100) return
        const frame = event.source as Window
        const reply = (card: unknown, error = false, status = card ? 'ready' : 'pending') => {
          if (currentReviewRef.current !== `${review.id}:${focusedDocumentId || ''}` || iframeRef.current?.contentWindow !== frame) return
          frame.postMessage({ type: 'mcp-ui:message-actions', request_id: data.request_id,
            agent_id: review.chat_id, message_id: messageId, card, error, status }, targetOrigin)
        }
        const invoke = data.type === 'mcp-ui:invoke-message-action'
        if (invoke && data.action_id !== 'review-documents') { reply(null, true); return }
        const routing = !invoke && typeof data.content === 'string' && data.content.length <= 2_000_000 ? parseRouting(data.content) : null
        if (!invoke && !routing) { reply(null, false, 'none'); return }
        const key = `${review.id}:${messageId}`
        let request = packetRequestsRef.current.get(key)
        if (!request) {
          request = (async () => {
            const endpoint = `/api/supply/reviews/${review.id}/messages/${messageId}/summary`
            let result = await fetchAPI(endpoint)
            if (!result.data && routing) {
              const saved = await fetchAPI(`/api/supply/reviews/${review.id}/result`, {
                method: 'POST', body: JSON.stringify({ message_id: messageId, created_at: data.created_at, documents: routing.documents }),
              })
              if (saved.error || saved.errors) throw new Error('Не удалось сохранить результат')
              if (currentReviewRef.current === `${review.id}:${focusedDocumentId || ''}`) {
                if (syncTimerRef.current) clearTimeout(syncTimerRef.current)
                syncTimerRef.current = setTimeout(() => { void syncResults() }, 350)
              }
              result = await fetchAPI(endpoint)
            }
            if (result.error || result.errors) throw new Error('Результат недоступен')
            return result.data as ReviewPacketSummary | null
          })().finally(() => { packetRequestsRef.current.delete(key) })
          packetRequestsRef.current.set(key, request)
        }
        void request.then(summary => {
          if (!summary) { reply(null, invoke); return }
          reply(reviewMessageActionCard(summary))
          if (invoke && currentReviewRef.current === `${review.id}:${focusedDocumentId || ''}` && iframeRef.current?.contentWindow === frame) {
            navigate(`/warehouse/reviews/${review.id}?document=${encodeURIComponent(summary.document_id)}&from=chat`)
          }
        }).catch(() => reply(null, true))
        return
      }
      if (data?.type === 'mcp-ui:assistant-message') {
        const routing = parseRouting(data.content)
        const messageId = String(data.message_id || '')
        const key = `${review.id}:${messageId}`
        if (!routing || !messageId || processedMessagesRef.current.has(key)) return
        processedMessagesRef.current.add(key)
        if (syncTimerRef.current) clearTimeout(syncTimerRef.current)
        syncTimerRef.current = setTimeout(() => {
          void syncResults().then(() => {
            if (currentReviewRef.current !== `${review.id}:${focusedDocumentId || ''}`) return
            iframeRef.current?.contentWindow?.postMessage({
              type: 'mcp-ui:message-actions-updated', agent_id: review.chat_id, message_id: messageId,
            }, targetOrigin)
          })
        }, 350)
      }
    }
    window.addEventListener('message', onMessage)
    return () => { window.removeEventListener('message', onMessage); if (syncTimerRef.current) clearTimeout(syncTimerRef.current) }
  }, [review?.id, review?.chat_id, sendHostConfig, targetOrigin, syncResults, focusedDocumentId, navigate])

  const frameUrl = useMemo(() => {
    if (!review?.chat_id) return ''
    const path = `/ai/embed/chats/${review.chat_id}?compact=1`
    const base = getAi2UiBaseUrl()
    return base ? buildMcpUiUrl(path, base) : path
  }, [review?.chat_id])

  const reviewDate = (item: Review) => new Date(item.created_at).toLocaleString('ru-RU', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  })

  return (
    <B2BLayout breadcrumbs={[{ href: '/warehouse', title: 'Склад' }, { title: 'Разбор документов' }]} pageTitle="Разбор документов">
      {loading && <Skeleton active />}
      {!loading && error && <Result status="error" title="Разбор не открыт" subTitle={error} />}
      {!loading && !error && <div className="supply-review">
        {(!canOpenChat || view === 'check') && review ? <SupplyReviewWorkbench key={`${review.id}:${focusedDocumentId || 'chat'}`} initialDocumentId={focusedDocumentId} backLabel={canOpenChat && (!focusedDocumentId || fromChat) ? 'Вернуться в чат' : 'Входящие накладные'} onOpenChat={canOpenChat && focusedDocumentId ? () => navigate(`/warehouse/reviews/${review.id}`) : undefined} focusOriginal={hash === '#original-document'} reviewId={review.id} documents={documents} files={sourceFiles} error={resultsError} currentUserId={currentUserId} onPoll={async () => { await loadResults() }} onSaved={saved => setDocuments(items => items.map(item => item.id === saved.id ? saved : item))} onRefresh={async () => { await syncResults() }} onBack={() => !canOpenChat || focusedDocumentId ? navigate(canOpenChat && fromChat ? `/warehouse/reviews/${review.id}` : incomingListPath) : setView('chat')} /> : !canOpenChat ? <Empty description="Загрузите документы во входящих накладных"><Button onClick={() => navigate(incomingListPath)}>Входящие накладные</Button></Empty> : <div className="supply-review__layout">
          <aside className="supply-review__threads">
            <div className="supply-review__threads-header">
              <div className="supply-review__threads-title">
                <Typography.Title level={5}>Общие чаты разбора</Typography.Title>
                <Button type="text" size="small" icon={<ReloadOutlined />} loading={refreshingReviews} aria-label="Обновить список чатов" onClick={refreshReviews} />
              </div>
              <Button type="primary" icon={<PlusOutlined />} block loading={creatingReview} disabled={!purposeId} onClick={createNewReview}>
                Новый чат
              </Button>
            </div>
            {(purposeError || listError) && <Alert type="warning" showIcon message={purposeError || listError} className="supply-review__threads-error" />}
            <nav className="supply-review__threads-list" aria-label="Чаты разбора документов">
              {reviews.map(item => <Link
                key={item.id}
                to={`/warehouse/reviews/${item.id}`}
                className={`supply-review__thread${review?.id === item.id ? ' is-active' : ''}`}
                aria-current={review?.id === item.id ? 'page' : undefined}
              >
                <span className="supply-review__thread-title" title={item.chat_id ? chatNames[item.chat_id] : undefined}>{item.chat_id && chatNames[item.chat_id] || 'Новый разбор'}</span>
                <span className="supply-review__thread-meta">{[reviewDate(item), item.creator_name].filter(Boolean).join(' · ')}</span>
              </Link>)}
              {!reviews.length && <div className="supply-review__threads-empty">Пока нет чатов. Создайте первый разбор.</div>}
            </nav>
          </aside>
          <div className="supply-review__workspace">
      {review ? <>
        <div className="supply-review__heading">
          <div>
            <Typography.Text type="secondary">Общий чат для входящих документов</Typography.Text>
            <Typography.Title level={4} style={{ margin: '2px 0 0' }}>{review.chat_id && chatNames[review.chat_id] || 'Разбор документов'}</Typography.Title>
          </div>
          <div className="supply-review__actions"><Button onClick={() => navigate('/warehouse/incoming')}>Входящие накладные</Button>
            {resultsError && <TooltipResultError text={resultsError} />}
            <Button icon={<CheckSquareOutlined />} loading={resultsLoading} onClick={() => setView('check')}>Проверить накладные{documents.length ? ` · ${documents.length}` : ''}</Button>
            {review.chat_id && <Button icon={<ReloadOutlined />} onClick={() => { setFrameLoading(true); setFrameVersion(value => value + 1) }}>Обновить чат</Button>}
          </div>
        </div>
        <div className="supply-review__grid">
          <section className="supply-review__chat" aria-label="Чат Светофора">
            {review.chat_id ? <>
              {frameLoading && <div className="supply-review__loader"><Spin /></div>}
              <iframe key={`${review.id}-${frameVersion}`} ref={iframeRef} src={frameUrl} title="Чат Светофора для разбора документов" onLoad={() => { setFrameLoading(false); void sendHostConfig() }} />
            </> : <div className="supply-review__empty"><Button type="primary" loading={creatingChat} disabled={!purposeId} onClick={createChat}>Создать чат</Button></div>}
          </section>
        </div>
      </> : <div className="supply-review__workspace-empty"><Empty description="Выберите чат слева или создайте новый" /></div>}
          </div>
        </div>}
      </div>}
    </B2BLayout>
  )
}
